import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ChangelogEntry {
  version: string;
  date: string;
  label: 'major' | 'minor' | 'patch' | 'hotfix';
  summary: string;
  changes: {
    type: 'added' | 'fixed' | 'improved' | 'removed' | 'security';
    text: string;
  }[];
}

const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.0.0',
    date: 'April 13, 2026',
    label: 'major',
    summary: 'Production release — full domain migration to apimarts.com and platform hardening.',
    changes: [
      { type: 'added', text: 'Changelog admin panel to track version history and release notes over time' },
      { type: 'added', text: 'Custom SMTP configuration with live test-send feature from admin panel' },
      { type: 'added', text: 'Email template editor for activation and renewal reminder emails with HTML + variable support' },
      { type: 'added', text: 'NOWPayments IPN webhook monitor with real-time payment status tracking in Logs tab' },
      { type: 'added', text: 'Stripe checkout session creation with full webhook handler for payment lifecycle' },
      { type: 'added', text: 'Subscription auto-expiry cron job that deactivates expired subscriptions daily' },
      { type: 'added', text: 'Daily admin report edge function with platform-wide revenue and subscription stats' },
      { type: 'added', text: 'Invoice PDF generation with APIMarts branding and payment details' },
      { type: 'improved', text: 'SMTP edge functions migrated from deno.land/x/smtp to raw TCP implementation — fully self-contained, no external deps' },
      { type: 'improved', text: 'Domain migration: all references updated from test.apimarts.com and apimart.io to apimarts.com' },
      { type: 'improved', text: 'Admin Settings default base URL and support email pre-configured for apimarts.com' },
      { type: 'improved', text: 'Developer API docs and test panel now point to https://api.apimarts.com/v1/{slug}' },
      { type: 'fixed', text: 'Vercel SPA routing — added vercel.json rewrites so direct URL access to /marketplace, /dashboard, etc. no longer returns 404' },
      { type: 'fixed', text: 'SMTP "Name or service not known" error — welcome-email edge function was using a broken third-party library; replaced with native Deno TCP SMTP' },
      { type: 'fixed', text: 'SITE_URL fallback in NOWPayments and Stripe edge functions now correctly uses apimarts.com' },
      { type: 'security', text: 'All SMTP credentials stored in Supabase smtp_settings table — never exposed to frontend' },
      { type: 'security', text: 'Payment provider API keys stored in Supabase payment_providers table with admin-only access' },
    ],
  },
  {
    version: '0.9.5',
    date: 'April 8, 2026',
    label: 'minor',
    summary: 'Admin panel enhancements, subscription management, and payment provider configuration.',
    changes: [
      { type: 'added', text: 'Payment providers tab with NOWPayments and Stripe API key management' },
      { type: 'added', text: 'Subscription management tab with status filters and bulk actions' },
      { type: 'added', text: 'System logs tab with real-time event monitoring and IPN filter' },
      { type: 'added', text: 'Notifications tab sourced from system_logs for live event feed' },
      { type: 'improved', text: 'Admin analytics dashboard with 6-month revenue chart and category breakdown' },
      { type: 'improved', text: 'Orders tab with CSV export, search, and status filter' },
      { type: 'fixed', text: 'Admin route guard — non-admin users redirected correctly to dashboard' },
    ],
  },
  {
    version: '0.9.0',
    date: 'April 1, 2026',
    label: 'minor',
    summary: 'Developer portal, API key management, and marketplace product listing.',
    changes: [
      { type: 'added', text: 'Developer portal with API key generation, usage stats, and live test panel' },
      { type: 'added', text: 'Marketplace page with product cards, category filters, and plan selection' },
      { type: 'added', text: 'API documentation tab with endpoint reference and code snippets' },
      { type: 'added', text: 'Account page with profile editing and password change' },
      { type: 'improved', text: 'Sidebar navigation with active state indicators and collapsible sections' },
      { type: 'fixed', text: 'Auth token refresh loop on page reload' },
    ],
  },
  {
    version: '0.8.0',
    date: 'March 22, 2026',
    label: 'minor',
    summary: 'Authentication system, protected routes, and user profile setup.',
    changes: [
      { type: 'added', text: 'Full auth flow: register, login, forgot password, reset password, email verify' },
      { type: 'added', text: 'Protected route wrapper with session-based access control' },
      { type: 'added', text: 'User profile table in Supabase with joined_at tracking' },
      { type: 'added', text: 'Dashboard page with real-time user stats and recent activity' },
      { type: 'improved', text: 'Auth context provider with auto session refresh' },
      { type: 'security', text: 'Row-level security policies on all user tables' },
    ],
  },
  {
    version: '0.5.0',
    date: 'March 10, 2026',
    label: 'minor',
    summary: 'Project scaffold, design system, and landing page.',
    changes: [
      { type: 'added', text: 'React 19 + TypeScript + Vite + TailwindCSS project setup' },
      { type: 'added', text: 'Landing page with hero section, features, pricing, and CTA' },
      { type: 'added', text: 'AppLayout with sidebar, top bar, and responsive mobile menu' },
      { type: 'added', text: 'Supabase integration with singleton client pattern' },
      { type: 'added', text: 'Brand color system and typography scale in Tailwind config' },
    ],
  },
];

const LABEL_STYLE: Record<ChangelogEntry['label'], string> = {
  major: 'bg-brand-500 text-white',
  minor: 'bg-emerald-500 text-white',
  patch: 'bg-slate-500 text-white',
  hotfix: 'bg-red-500 text-white',
};

const CHANGE_ICON: Record<string, string> = {
  added: 'ri-add-circle-line',
  fixed: 'ri-bug-line',
  improved: 'ri-arrow-up-circle-line',
  removed: 'ri-delete-bin-line',
  security: 'ri-shield-check-line',
};

const CHANGE_COLOR: Record<string, string> = {
  added: 'text-emerald-600',
  fixed: 'text-amber-600',
  improved: 'text-brand-500',
  removed: 'text-red-500',
  security: 'text-slate-600',
};

const CHANGE_BG: Record<string, string> = {
  added: 'bg-emerald-50',
  fixed: 'bg-amber-50',
  improved: 'bg-brand-50',
  removed: 'bg-red-50',
  security: 'bg-slate-100',
};

export default function ChangelogTab() {
  const [expanded, setExpanded] = useState<string | null>('1.0.0');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const allTypes = ['all', 'added', 'fixed', 'improved', 'security', 'removed'];
  const latest = CHANGELOG[0];

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Header banner */}
      <div className="bg-white rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${LABEL_STYLE[latest.label]}`}>{latest.label}</span>
              <span className="text-xs text-gray-400">Latest</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">v{latest.version}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{latest.summary}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-black text-brand-500">v{latest.version}</div>
            <div className="text-xs text-gray-400 mt-0.5">{latest.date}</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(['added', 'fixed', 'improved', 'security'] as const).map(type => {
            const count = latest.changes.filter(c => c.type === type).length;
            return (
              <div key={type} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${CHANGE_BG[type]}`}>
                <i className={`${CHANGE_ICON[type]} ${CHANGE_COLOR[type]} text-base`} />
                <div>
                  <div className={`text-sm font-bold ${CHANGE_COLOR[type]}`}>{count}</div>
                  <div className="text-[10px] text-gray-500 capitalize">{type}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        {allTypes.map(t => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize cursor-pointer whitespace-nowrap transition-colors ${typeFilter === t ? 'bg-sidebar text-white' : 'bg-white border border-surface-3 text-gray-500 hover:border-gray-300'}`}
          >
            {t === 'all' ? 'All Changes' : (
              <span className="flex items-center gap-1">
                <i className={`${CHANGE_ICON[t]} ${typeFilter === t ? 'text-white' : CHANGE_COLOR[t]}`} />
                {t}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative space-y-4">
        {/* Vertical line */}
        <div className="absolute left-[19px] top-6 bottom-6 w-px bg-surface-3" />

        {CHANGELOG.map((entry, idx) => {
          const visibleChanges = typeFilter === 'all'
            ? entry.changes
            : entry.changes.filter(c => c.type === typeFilter);

          if (typeFilter !== 'all' && visibleChanges.length === 0) return null;

          const isOpen = expanded === entry.version;

          return (
            <motion.div
              key={entry.version}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.06 }}
              className="relative pl-10"
            >
              {/* Dot */}
              <div className={`absolute left-2.5 top-4 w-4 h-4 rounded-full border-2 border-white z-10 ${idx === 0 ? 'bg-brand-500' : 'bg-gray-300'}`} />

              <div className="bg-white rounded-xl overflow-hidden">
                {/* Header */}
                <button
                  onClick={() => setExpanded(isOpen ? null : entry.version)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface-2 transition-colors cursor-pointer"
                >
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide whitespace-nowrap ${LABEL_STYLE[entry.label]}`}>{entry.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-bold text-gray-900">v{entry.version}</span>
                      <span className="text-xs text-gray-400">{entry.date}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.summary}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-400 hidden sm:block">{entry.changes.length} changes</span>
                    <i className={`text-gray-400 text-sm transition-transform ${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                  </div>
                </button>

                {/* Change list */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-surface-3 px-5 py-4 space-y-2">
                        {visibleChanges.map((change, ci) => (
                          <motion.div
                            key={ci}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: ci * 0.03 }}
                            className="flex items-start gap-2.5"
                          >
                            <span className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 mt-0.5 ${CHANGE_BG[change.type]}`}>
                              <i className={`${CHANGE_ICON[change.type]} ${CHANGE_COLOR[change.type]} text-xs`} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <span className={`text-[10px] font-semibold uppercase tracking-wide mr-2 ${CHANGE_COLOR[change.type]}`}>{change.type}</span>
                              <span className="text-sm text-gray-700">{change.text}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm text-amber-700">
        <i className="ri-information-line mt-0.5 shrink-0" />
        <span>This changelog is manually maintained. Add new entries to <strong>ChangelogTab.tsx</strong> with each deployment to keep a complete release history.</span>
      </div>
    </div>
  );
}
